import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Flag, MessageCircle, Send, VolumeX, Volume2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errCode } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";

const QUICK = ["gg", "luck", "nice", "oops", "hurry", "thanks", "again", "wow"];
const EMOJIS = ["😀", "😂", "😎", "😮", "😢", "😡", "👍", "👏", "🔥", "🎲", "🏆", "🙏"];
const MUTE_KEY = "bkm-chat-muted";
const readMuted = () => { try { return JSON.parse(localStorage.getItem(MUTE_KEY)) || []; } catch { return []; } };

const useChat = (kind, gid) => {
  const [msgs, setMsgs] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const last = useRef(null);
  const tick = useRef(0);
  const poll = useCallback(() => {
    const full = tick.current++ % 5 === 0;
    return api.get(`/chat/${kind}/${gid}`, { params: !full && last.current ? { after: last.current } : {} }).then(({ data }) => {
      setEnabled(data.enabled);
      setLoaded(true);
      if (full) { setMsgs(data.messages); last.current = data.messages.at(-1)?.created_at || null; return; }
      if (!data.messages.length) return;
      last.current = data.messages[data.messages.length - 1].created_at;
      setMsgs((m) => [...m, ...data.messages.filter((x) => !m.some((y) => y.id === x.id))]);
    }).catch(() => {});
  }, [kind, gid]);
  useEffect(() => { last.current = null; tick.current = 0; setMsgs([]); setLoaded(false); poll(); const i = setInterval(poll, 3000); return () => clearInterval(i); }, [poll]);
  return { msgs, enabled, poll, loaded };
};

const Bubble = ({ m, mine, onMute, onReport, t }) => (
  <div className={`flex flex-col ${mine ? "items-end" : "items-start"}`} data-testid={`chat-msg-${m.id}`}>
    {!mine && (
      <span className="flex items-center gap-1 text-[11px] text-slate-400">{m.pseudo}
        <button type="button" onClick={onMute} title={t("chat.mute")} className="rounded-full p-1.5 hover:bg-white/10" data-testid={`chat-mute-${m.id}`}><VolumeX className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={onReport} title={t("chat.report")} className="rounded-full p-1.5 hover:bg-white/10" data-testid={`chat-report-${m.id}`}><Flag className="h-3.5 w-3.5" /></button>
      </span>
    )}
    <span className={`max-w-[85%] break-words rounded-2xl px-3 py-1.5 ${m.type === "emoji" ? "text-2xl" : "text-sm"} ${mine ? "bg-amber-500 text-[#0B0D12]" : "bg-white/10"}`}>
      {m.type === "quick" ? t(`chat.q_${m.text}`) : m.text}
    </span>
  </div>
);

const Composer = ({ send, t }) => {
  const [text, setText] = useState("");
  return (
    <div className="space-y-2 border-t border-white/10 p-3">
      <div className="flex gap-1 overflow-x-auto pb-1">{QUICK.map((q) => <button key={q} type="button" onClick={() => send("quick", q)} className="shrink-0 rounded-full border border-white/10 px-3 py-1 text-xs" data-testid={`chat-quick-${q}`}>{t(`chat.q_${q}`)}</button>)}</div>
      <div className="flex flex-wrap gap-1">{EMOJIS.map((e) => <button key={e} type="button" onClick={() => send("emoji", e)} className="text-xl transition-transform hover:scale-125" data-testid={`chat-emoji-${EMOJIS.indexOf(e)}`}>{e}</button>)}</div>
      <form className="flex gap-2" onSubmit={(ev) => { ev.preventDefault(); if (text.trim()) { send("text", text.trim()); setText(""); } }}>
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={300} placeholder={t("chat.placeholder")} className="h-10 min-w-0 flex-1 rounded-full border border-white/10 bg-white/5 px-4 text-sm outline-none focus:border-amber-500/60" data-testid="chat-input" />
        <button type="submit" className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500 text-[#0B0D12]" data-testid="chat-send-btn"><Send className="h-4 w-4" /></button>
      </form>
    </div>
  );
};

export const GameChat = ({ kind, gid }) => {
  const { t, tErr } = useI18n();
  const { user } = useAuth();
  const { msgs, enabled, poll, loaded } = useChat(kind, gid);
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(0);
  const [muted, setMuted] = useState(readMuted);
  const end = useRef(null);
  const known = useRef(null);
  const [pops, setPops] = useState([]);
  const visible = msgs.filter((m) => !muted.includes(m.user_id));
  useEffect(() => {
    if (!loaded) return;
    if (known.current === null) { known.current = new Set(msgs.map((m) => m.id)); return; }
    const fresh = visible.filter((m) => !known.current.has(m.id));
    msgs.forEach((m) => known.current.add(m.id));
    const emo = fresh.filter((m) => m.type === "emoji");
    if (!emo.length) return;
    setPops((p) => [...p, ...emo.map((m) => ({ id: m.id, e: m.text, x: 30 + Math.random() * 40 }))]);
    emo.forEach((m) => setTimeout(() => setPops((p) => p.filter((x) => x.id !== m.id)), 1400));
  }, [msgs, visible, loaded]);
  const others = visible.filter((m) => m.user_id !== user?.id).length;
  const unread = open ? 0 : Math.max(0, others - seen);
  useEffect(() => { if (open) { setSeen(others); end.current?.scrollIntoView({ block: "end" }); } }, [open, others, visible.length]);
  if (!enabled || !user) return null;
  const send = async (type, text) => { try { await api.post(`/chat/${kind}/${gid}`, { type, text }); poll(); } catch (e) { toast.error(tErr(errCode(e))); } };
  const mute = (uid, on) => { const next = on ? [...new Set([...muted, uid])] : muted.filter((x) => x !== uid); setMuted(next); localStorage.setItem(MUTE_KEY, JSON.stringify(next)); };
  const report = async (m) => {
    const reason = window.prompt(t("chat.reportReason"));
    if (reason === null) return;
    try { await api.post(`/chat/messages/${m.id}/report`, { reason }); toast.success(t("chat.reported")); } catch (e) { toast.error(tErr(errCode(e))); }
  };
  const mutedHere = [...new Set(msgs.filter((m) => muted.includes(m.user_id)).map((m) => m.user_id))];
  return (
    <>
      {createPortal(
        <>
          <div className="pointer-events-none fixed inset-0 z-50" aria-hidden="true">
            {pops.map((p) => <span key={p.id} className="emoji-pop absolute top-[38%] text-[96px] leading-none" style={{ left: `${p.x}%` }} data-testid="chat-emoji-pop">{p.e}</span>)}
          </div>
          {open && (
        <div className="fixed right-3 top-20 z-40 flex max-h-[60vh] w-[calc(100vw-1.5rem)] max-w-sm flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#121620]/95 shadow-2xl backdrop-blur-xl md:right-6 md:top-24" data-testid="chat-panel">
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-2">
            <p className="font-display font-bold">{t("chat.title")}</p>
            <div className="flex items-center gap-2">
              {mutedHere.length > 0 && <button type="button" onClick={() => mutedHere.forEach((u) => mute(u, false))} className="flex items-center gap-1 text-xs text-slate-400" data-testid="chat-unmute-all"><Volume2 className="h-3 w-3" />{t("chat.unmute")}</button>}
              <button type="button" onClick={() => setOpen(false)} className="rounded-full p-1 text-slate-400" data-testid="chat-close-btn"><X className="h-4 w-4" /></button>
            </div>
          </div>
          <div className="flex-1 space-y-2 overflow-y-auto p-3" data-testid="chat-messages">
            {visible.length === 0 && <p className="py-6 text-center text-xs text-slate-500" data-testid="chat-empty">{t("chat.empty")}</p>}
            {visible.map((m) => <Bubble key={m.id} m={m} mine={m.user_id === user.id} onMute={() => mute(m.user_id, true)} onReport={() => report(m)} t={t} />)}
            <div ref={end} />
          </div>
          <Composer send={send} t={t} />
        </div>
          )}
        </>,
        document.body,
      )}
      <button type="button" onClick={() => setOpen(!open)} data-testid="chat-toggle-btn" aria-label={t("chat.title")}
        className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500 text-[#0B0D12] transition-transform active:scale-95">
        {open ? <X className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />}
        {unread > 0 && <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white" data-testid="chat-unread">{unread}</span>}
      </button>
    </>
  );
};
